import { Link } from '@tanstack/react-router'
import { StatusDot } from '@/components/ui/status-dot'
import { Badge } from '@/components/ui/badge'
import { statusLabels, statusVariants } from '@/lib/status'
import { isColleagueViewer } from '@/lib/scope'
import { formatTicketRef } from '@/lib/ticketRef'
import type { Ticket, ViewerRelation } from '@/types/ticket'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(relativeTime)

// V2 44px truncating table row (table.css): fixed height, hairline
// dividers between rows (from the list wrapper's divide-y), micro-weight
// secondary columns, and the status column as a tinted badge instead of
// plain text — matching the mockup's Tickets inbox pills.
export function TicketRow({
  ticket,
  relation,
  hrefBase = '/tickets',
  asUser,
}: {
  ticket: Ticket
  relation: ViewerRelation
  hrefBase?: string
  asUser?: string
}) {
  const reporterName = ticket.creator?.name ?? null

  return (
    <Link
      to={`${hrefBase}/${ticket._id}` as never}
      search={{ asUser } as never}
      className="group flex h-11 items-center gap-3 px-4 text-etn-control transition-colors hover:bg-etn-row-hover"
    >
      <StatusDot status={ticket.status} />
      <span className="w-16 shrink-0 text-etn-micro text-etn-text-soft tabular-nums hidden sm:inline">
        {formatTicketRef(ticket)}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate font-semibold text-etn-text">{ticket.subject}</span>
          {isColleagueViewer(relation) && (
            <span className="rounded-etn-menu bg-etn-neutral-subtle px-1.5 py-px text-etn-micro text-etn-neutral-text">
              Colleague&#39;s
            </span>
          )}
        </div>
        {reporterName && (
          <div className="text-etn-micro text-etn-text-soft truncate">{reporterName}</div>
        )}
      </div>
      <Badge variant={statusVariants[ticket.status]} className="hidden sm:inline-flex">
        {statusLabels[ticket.status]}
      </Badge>
      <span className="w-14 text-right text-etn-micro text-etn-text-soft tabular-nums">
        {dayjs(ticket.lastMessageAt).fromNow(true)}
      </span>
    </Link>
  )
}
